<template>
  <RouterView />
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { userService } from './services/user'
import { useAuthStore } from './stores/auth-store'
import { useUserStore } from './stores/user-store'
import { login } from './services/login'

const router = useRouter()
const authStore = useAuthStore()
const userStore = useUserStore()
const userInfoPoller = ref<{ stop: () => void } | null>(null)

// 启动用户信息轮询
const startUserInfoPolling = () => {
  // 停止现有的轮询（如果有）
  if (userInfoPoller.value) {
    userInfoPoller.value.stop()
  }

  // 开始新的轮询
  userInfoPoller.value = userService.startPollingUserInfo(60000)
}

// 停止用户信息轮询
const stopUserInfoPolling = () => {
  if (userInfoPoller.value) {
    userInfoPoller.value.stop()
    userInfoPoller.value = null
  }
}

// 自定义轮询函数，移除导航逻辑
const startCustomPolling = () => {
  let timerId: number | null = null

  // 定义轮询函数
  const poll = async () => {
    try {
      const userInfo = await userService.getUserInfo()

      // 保存用户信息到store
      userStore.setUserInfo(userInfo)

      // 移除自动导航到preferences的逻辑
    } catch (error) {
      // 出错时跳转到登录页面
      login.logout()
    }
  }

  // 立即执行一次
  poll()

  // 设置定时器
  timerId = window.setInterval(poll, 60000)

  // 返回停止函数
  return {
    stop: () => {
      if (timerId !== null) {
        window.clearInterval(timerId)
        timerId = null
      }
    },
  }
}

// 监听认证状态变化
watch(
  () => authStore.isAuthenticated,
  (newValue) => {
    if (newValue) {
      // 用户已登录，开始轮询
      userInfoPoller.value = startCustomPolling()
    } else {
      // 用户已登出，停止轮询
      stopUserInfoPolling()
      // 清除用户信息
      userStore.clearUserInfo()
    }
  },
  { immediate: true },
)

// 在组件挂载时启动轮询（如果用户已认证）
onMounted(() => {
  if (authStore.isAuthenticated) {
    userInfoPoller.value = startCustomPolling()
  }
})

// 在组件卸载时停止轮询
onUnmounted(() => {
  stopUserInfoPolling()
})
</script>

<style lang="scss">
#app {
  font-family: 'Inter', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  margin: 0;
  min-width: 20rem;
}
</style>
